"use client";

import React, {useEffect, useState} from 'react';
import Link from 'next/link';
import {MessageCircle, Phone} from 'lucide-react';
import NavbarMobile from "@/components/NavBarMobile";
import FormLead from "@/components/FormLead";
import Image from "next/image";
import {useSelector} from "react-redux";
import {RootState} from "@/redux/store";
import {Badge} from "@/components/ui/badge";
import {VeicoloData} from "@/interface/VeicoloData";
import {formatPrice} from "@/utils/format-price";
import {motion} from "framer-motion"
import {useRouter} from "next/navigation";
import {useSession} from "next-auth/react";
import { usePathname } from 'next/navigation';

import Cookies from "js-cookie";
import SearchBoxHome from "@/components/Home/SearchBoxhome";


interface NavbarProps {
    showEllipse?: boolean | undefined;
}

const Navbar: React.FC<NavbarProps> = ({showEllipse = false}) => {

    const [cartOpen, setCartOpen] = useState(false);
    const vehiclesInCart = useSelector((state: RootState) => state.cart.vehicles);
    const [prevCartLength, setPrevCartLength] = useState(0);
    const router = useRouter();
    const {data: session, status} = useSession()

    const [isScrolled, setIsScrolled] = useState(false);

    const [menuOpen, setMenuOpen] = useState(false);

    const [leadFrom, setLeadFrom] = useState(false);
    const [telephone, setTelephone] = useState(false);
    const [isClient, setIsClient] = useState(false);

    const userData = Cookies.get("userData") ? JSON.parse(Cookies.get("userData")!) : null;
    const checkoutUrl = userData?.infoUtentiTipo === "COM" ? "/checkout-b2b" : "/checkout";
    const pathname = usePathname();


    useEffect(() => {
        // Indica che il componente è stato montato sul client
        setIsClient(true);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        // Add the scroll event listener when the component mounts
        window.addEventListener('scroll', handleScroll);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const toggleCart = () => {
        setCartOpen(!cartOpen);
    };

    const handleAccount = () => {
        if (pathname === '/diventa-partner-commercianti' || pathname === '/diventa-partner') {
            window.location.href = 'https://www.consorzioauto.com/';
            return;
        }

        if (status === 'loading') return;

        if (status === 'authenticated') {
            router.push('/account/profilo');
        } else {
            router.push('/auth/accedi');
        }
    }


    /*
    <nav
                className={`z-40 fixed top-0 left-0 w-full max-lg:hidden transition duration-300 ease-in-out ${
                    menuOpen || isScrolled ? 'backdrop-blur-md bg-white/30' : 'bg-transparent'
                }`}
            >

    */
    return (
        <>
            <div className={`z-40 fixed top-0 left-0 w-full max-lg:hidden transition duration-300 ease-in-out ${
                menuOpen || isScrolled ? 'backdrop-blur-md bg-white/30' : 'bg-transparent'
            }`}>
                {/* Top Bar */}
                <div className=" flex items-center justify-between px-4 py-3 border-b">
                    <div className="flex items-center gap-10">
                        <Link href='/'>
                            <Image
                                src={showEllipse && !isScrolled ? "/logo_bianco.png" : "/logo-vettoriale.svg"}
                                alt="Gruppo Piccirillo logo"
                                width={70} // Specifica la larghezza
                                height={70} // Specifica l'altezza
                                priority={true} // Precarica l'immagine perché è un elemento critico nella UI
                                className="h-10 w-auto" // Tailwind per aggiustare ulteriormente lo stile
                            />
                        </Link>

                        <nav className="flex items-center gap-6 text-lg font-medium">
                            <Link
                                hrefLang="it"
                                href="/parco-veicoli"
                                title="Parco Veicoli - Catalogo dei Veicoli Acquistabili"
                                className={`${showEllipse && !isScrolled ? 'text-white' : 'text-gray-800'} text-xl max-md:text-base hover:text-primary transition duration-300`}
                            >
                                Acquista
                            </Link>
                            <Link
                                hrefLang="it"
                                href="/vendi-la-tua-auto/permuta"
                                title="Vendi la tua auto"
                                className={`${showEllipse && !isScrolled ? 'text-white' : 'text-gray-800'} text-xl hover:text-primary transition duration-300`}
                            >
                                Vendi
                            </Link>
                            <Link
                                hrefLang="it"
                                href="/rent-to-buy"
                                title="Rent to Buy - Catalogo dei Veicoli Noleggiabili"
                                className={`${showEllipse && !isScrolled ? 'text-white' : 'text-gray-800'} text-xl hover:text-primary transition duration-300`}
                            >
                                Rent to Buy
                            </Link>
                            <Link
                                hrefLang="it"
                                href="/offerta-last-minute"
                                title="Offerte last minute"
                                className={`${showEllipse && !isScrolled ? 'text-white' : 'text-gray-800'} text-xl hover:text-primary transition duration-300`}
                            >
                                Offerte last minute
                            </Link>
                            <Link hrefLang="it" href={'/convenzioni'} title="Convenzioni"
                                  className="bg-gradient rounded-lg px-2 text-white  text-xl transition duration-300">Convenzioni
                            </Link>
                        </nav>

                    </div>

                    <div className="flex items-center gap-4 relative">
                        {
                            (isScrolled || menuOpen) && (
                                <SearchBoxHome className="!py-1 mx-2"/>
                            )
                        }
                        <div className="relative" onClick={toggleCart}>
                            <svg width="27" height="25" viewBox="0 0 27 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M0.0183924 1.34385C0.119968 0.835973 0.614029 0.506599 1.12191 0.608175L2.43404 0.870601C3.91609 1.16701 5.03084 2.3964 5.18123 3.9003L5.28784 4.9664H22.2628C24.7439 4.9664 26.5644 7.29808 25.9626 9.70512L24.5587 15.3208C23.9673 17.6865 21.8417 19.3461 19.4031 19.3461H8.15829C6.41766 19.3461 4.95155 18.0454 4.74416 16.3172L3.50691 6.00674L3.31492 4.08693C3.24656 3.40334 2.73986 2.84453 2.0662 2.70979L0.754069 2.44737C0.24619 2.34579 -0.0831833 1.85173 0.0183924 1.34385ZM9.69081 8.65748C9.2766 8.65748 8.94081 8.99327 8.94081 9.40748C8.94081 9.82169 9.2766 10.1575 9.69081 10.1575H14.6925C15.1067 10.1575 15.4425 9.82169 15.4425 9.40748C15.4425 8.99327 15.1067 8.65748 14.6925 8.65748H9.69081Z" fill="#000D7D"/>
                                <circle cx="9.06543" cy="22.7847" r="1.87562" fill="#000D7D"/>
                                <circle cx="20.3192" cy="22.7847" r="1.87562" fill="#000D7D"/>
                            </svg>
                            {isClient && vehiclesInCart && vehiclesInCart.length > 0 && (
                                <Badge className="absolute -top-2 -right-2 !px-2 text-white border-0">
                                    {vehiclesInCart.length}
                                </Badge>
                            )}
                        </div>
                        <div className="relative" onClick={handleAccount}>
                            <svg className="w-8 h-8" width="45" height="44" viewBox="0 0 45 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_578_51)">
                                    <rect x="0.222656" width="44" height="44" rx="22" fill="#000D7D"/>
                                    <circle cx="8.11429" cy="8.11429" r="8.11429" transform="matrix(-1 0 0 1 30.337 11)" fill="#A6C2FF"/>
                                    <path d="M8.02344 39.176C8.02344 37.4856 9.08612 35.9776 10.6781 35.4091L11.1243 35.2497C18.3017 32.6863 26.1451 32.6863 33.3225 35.2497L33.7688 35.4091C35.3608 35.9776 36.4234 37.4856 36.4234 39.176V44.194C36.4234 45.4111 35.3455 46.346 34.1406 46.1739L26.0418 45.0169C23.5091 44.6551 20.9378 44.6551 18.4051 45.0169L10.3063 46.1739C9.10141 46.346 8.02344 45.4111 8.02344 44.194V39.176Z" fill="#A6C2FF"/>
                                </g>
                                <defs>
                                    <clipPath id="clip0_578_51">
                                        <rect x="0.222656" width="44" height="44" rx="22" fill="white"/>
                                    </clipPath>
                                </defs>
                            </svg>
                        </div>

                        {/*Cart Dropdown*/}
                        {cartOpen && (
                            <motion.div className="absolute right-1/2 top-[100%] bg-white/70 mt-2 w-64 bg-white rounded-lg p-1.5 shadow-lg z-10"
                                        initial={{opacity: 0, rotateX: -90, y: -20}}
                                        animate={{opacity: 1, rotateX: 0, y: 0, scale: 1}}
                                        exit={{opacity: 0, rotateX: 90, y: -20, scale: 0.95}}
                                        transition={{
                                            duration: 0.5,
                                            type: "spring",
                                            stiffness: 150,
                                            damping: 12
                                        }}
                                        style={{
                                            transformOrigin: "top center"
                                        }}
                            >
                                {vehiclesInCart.length === 0 ? (
                                    <div className="text-center p-4">
                                        <p className="text-sm font-bold text-black">Il carrello è vuoto</p>
                                    </div>
                                ) : (
                                    vehiclesInCart.map((vehicle: VeicoloData, index) => (
                                        <div key={index}
                                             className="rounded bg-white flex justify-between items-center p-2">
                                            <div className="flex-none relative">

                                                <p className="absolute top-0 left-0 text-[10px] px-1 text-[#979797]">1x</p>

                                                <Image
                                                    src={vehicle.images.find((image) => image.defaultimage == '1')?.image || '/autoinarrivo.webp'}
                                                    alt={vehicle.marca + ' ' + vehicle.modello}
                                                    className="rounded-lg object-cover"
                                                    width={50} // Adjusted to better match the intended width
                                                    height={50} // Adjusted to keep aspect ratio
                                                    style={{
                                                        width: '50px',
                                                        height: 'auto'
                                                    }} // Responsive styling
                                                />
                                            </div>

                                            <div className="flex flex-col justify-center flex-grow pl-2">
                                                <span className="text-xs leading-tight font-light">
                                                    {vehicle.marca}
                                                </span>
                                                <span className="text-sm font-bold">{vehicle.modelloAbr}</span>
                                            </div>

                                            <div className="flex-none">
                        <span
                            className="text-sm font-normal">{formatPrice(+vehicle.prezzo)}</span>
                                            </div>
                                        </div>
                                    ))
                                )}

                                {vehiclesInCart.length > 0 && (
                                    <div className="w-full pt-2 rounded">
                                        <Link onClick={toggleCart}
                                              hrefLang={'it'}
                                              href={checkoutUrl}
                                              title={'Carrello'}
                                              className="block w-full rounded text-white bg-gradient p-1 text-center"
                                        >
                                            Vai al checkout
                                        </Link>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className=" flex items-center justify-between px-4 py-2">
                    <div className="flex items-center gap-4">
<span className={`text-lg font-semibold ${showEllipse && !isScrolled ? 'text-white' : 'text-gray-800'}`}>
  Diventa Partner
</span>
                        <button onClick={() => {
                            router.push('/diventa-partner-commercianti');
                        }}
                                className="bg-primary text-white px-6 py-2 flex items-center">
                            Per commercianti
                        </button>
                        <button onClick={() => {
                            router.push('/diventa-partner');
                        }}
                                className="bg-primary text-white px-6 py-2 flex items-center">
                            Per noleggiatori
                        </button>
                    </div>

                    {/*<div className="flex items-center gap-10 text-lg font-medium">
                        <Link hrefLang="it" title="Area commercianti"
                              className="text-gray-800  font-bold text-xl hover:text-primary transition duration-300"
                              href="https://consorzioauto.com">Area commercianti</Link>
                        <Link hrefLang="it" title="F.A.Q"
                              className="text-gray-800  font-bold text-xl hover:text-primary transition duration-300"
                              href="/faq">F.A.Q.</Link>
                        <Link hrefLang="it" title="Lavora con noi"
                              className="text-gray-800  font-bold text-xl hover:text-primary transition duration-300"
                              href="/lavora-con-noi">Lavora con noi</Link>
                    </div>*/}

                    <div className="flex items-center gap-4">
                        <button onClick={() => {
                            setLeadFrom(true);
                            setTelephone(true)
                        }}
                                className="bg-primary text-white  px-6 py-2 flex items-center">
                            <Phone fill={'white'} size={18} className="mr-2"/>
                            Chiamaci
                        </button>
                        <button onClick={() => {
                            setLeadFrom(true);
                            setTelephone(false)
                        }}
                                className="bg-primary text-white px-6 py-2 flex items-center">
                            <MessageCircle fill={'white'} size={18} className="mr-2"/>
                            Scrivici
                        </button>
                    </div>
                </div>
            </div>


            <NavbarMobile isScrolled={isScrolled}/>

            {
                leadFrom && (
                    <FormLead textarea={true} articolo={''} title={'Richeidi informazioni'} marca={''} modello={''}
                              setOpen={setLeadFrom} open={leadFrom} phone={telephone}/>
                )
            }
        </>
    );
};

export default Navbar;
